import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const TABS = ['account', 'email', 'security'];

const UserProfileSettings = () => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const { user, profile, signOut, updateUserMetadata } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  // Account tab state
  const [accountForm, setAccountForm] = useState({
    fullName: '',
    phone: '',
    agency: '',
  });
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountMsg, setAccountMsg] = useState(null);

  // Email tab state
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState(null);

  // Security tab state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const isRTL = currentLanguage === 'ar';

  useEffect(() => {
    if (user || profile) {
      setAccountForm({
        fullName: user?.user_metadata?.full_name || profile?.fullName || profile?.name || '',
        phone: user?.user_metadata?.phone || profile?.phone || '',
        agency: user?.user_metadata?.agency || profile?.agency || '',
      });
      setNewEmail(user?.email || '');
    }
  }, [user, profile]);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const mockUser = profile || {
    name: user?.user_metadata?.full_name || user?.email || 'Utilisateur',
    role: profile?.role || user?.user_metadata?.role || 'agency_admin',
    agency: user?.user_metadata?.agency || '',
    avatar: null,
  };

  // ── Account update ──────────────────────────────────────────
  const handleAccountSave = async (e) => {
    e?.preventDefault();
    setAccountLoading(true);
    setAccountMsg(null);
    try {
      await updateUserMetadata({
        full_name: accountForm?.fullName,
        phone: accountForm?.phone,
        agency: accountForm?.agency,
      });
      setAccountMsg({ type: 'success', text: isRTL ? 'تم تحديث المعلومات بنجاح' : 'Informations mises à jour avec succès' });
    } catch (err) {
      setAccountMsg({ type: 'error', text: isRTL ? 'حدث خطأ أثناء التحديث' : 'Erreur lors de la mise à jour' });
    } finally {
      setAccountLoading(false);
    }
  };

  // ── Email update ─────────────────────────────────────────────
  const handleEmailSave = async (e) => {
    e?.preventDefault();
    setEmailLoading(true);
    setEmailMsg(null);
    try {
      const { error } = await supabase?.auth?.updateUser({ email: newEmail });
      if (error) throw error;
      setEmailMsg({ type: 'success', text: isRTL ? 'تم إرسال رابط التأكيد إلى بريدك الجديد' : 'Un lien de confirmation a été envoyé à votre nouvel email' });
    } catch (err) {
      setEmailMsg({ type: 'error', text: err?.message || (isRTL ? 'حدث خطأ' : 'Erreur lors de la mise à jour') });
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Password update ──────────────────────────────────────────
  const handlePasswordSave = async (e) => {
    e?.preventDefault();
    setPasswordMsg(null);
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      setPasswordMsg({ type: 'error', text: isRTL ? 'كلمتا المرور غير متطابقتين' : 'Les mots de passe ne correspondent pas' });
      return;
    }
    if (passwordForm?.newPassword?.length < 8) {
      setPasswordMsg({ type: 'error', text: isRTL ? 'يجب أن تكون كلمة المرور 8 أحرف على الأقل' : 'Le mot de passe doit contenir au moins 8 caractères' });
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: passwordForm?.newPassword });
      if (error) throw error;
      setPasswordMsg({ type: 'success', text: isRTL ? 'تم تغيير كلمة المرور بنجاح' : 'Mot de passe modifié avec succès' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: err?.message || (isRTL ? 'حدث خطأ' : 'Erreur lors du changement') });
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabLabels = {
    account: isRTL ? 'معلومات الحساب' : 'Informations du compte',
    email: isRTL ? 'البريد الإلكتروني' : 'Adresse email',
    security: isRTL ? 'الأمان وكلمة المرور' : 'Sécurité & mot de passe',
  };

  const tabIcons = { account: 'User', email: 'Mail', security: 'Lock' };

  const Alert = ({ msg }) => {
    if (!msg) return null;
    const isSuccess = msg?.type === 'success';
    return (
      <div className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm mb-4 ${isSuccess ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-destructive/10 text-destructive border border-destructive/20'}`}>
        <Icon name={isSuccess ? 'CheckCircle' : 'AlertCircle'} size={16} className="flex-shrink-0" />
        <span>{msg?.text}</span>
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-background flex ${isRTL ? 'flex-row-reverse' : 'flex-row'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`flex-1 flex flex-col min-h-screen transition-smooth ${isSidebarCollapsed ? (isRTL ? 'mr-20' : 'ml-20') : (isRTL ? 'mr-72' : 'ml-72')}`}>
        {/* Top header */}
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-warm-sm px-6 py-4 flex items-center justify-between gap-4">
          <BreadcrumbNavigation />
          <div className="flex items-center gap-3">
            <NotificationCenter notifications={[]} onNotificationClick={() => {}} onMarkAsRead={() => {}} />
            <UserProfileDropdown user={mockUser} onLogout={handleLogout} onLanguageChange={() => {}} />
          </div>
        </header>

        <main className="flex-1 p-6 max-w-3xl mx-auto w-full">
          {/* Page title */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="UserCog" size={20} className="text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  {isRTL ? 'إعدادات الملف الشخصي' : 'Paramètres du profil'}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {isRTL ? 'إدارة معلومات حسابك وأمانك' : 'Gérez vos informations et préférences de sécurité'}
                </p>
              </div>
            </div>
          </div>

          {/* Avatar section */}
          <div className="bg-card border border-border rounded-lg p-5 mb-6 flex items-center gap-4 shadow-warm-sm">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={28} className="text-primary" />
            </div>
            <div>
              <p className="font-semibold text-foreground text-base">{mockUser?.name || user?.email}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary capitalize">
                {mockUser?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-card border border-border rounded-lg shadow-warm-sm overflow-hidden">
            <div className="flex border-b border-border">
              {TABS?.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center gap-2 px-5 py-4 text-sm font-medium transition-smooth border-b-2 -mb-px ${
                    activeTab === tab
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tabIcons?.[tab]} size={16} />
                  <span className="hidden sm:inline">{tabLabels?.[tab]}</span>
                </button>
              ))}
            </div>

            <div className="p-6">
              {/* ── Account Tab ── */}
              {activeTab === 'account' && (
                <form onSubmit={handleAccountSave} className="space-y-5">
                  <Alert msg={accountMsg} />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'الاسم الكامل' : 'Nom complet'}
                    </label>
                    <input
                      type="text"
                      value={accountForm?.fullName}
                      onChange={(e) => setAccountForm({ ...accountForm, fullName: e?.target?.value })}
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                      placeholder={isRTL ? 'أدخل اسمك الكامل' : 'Entrez votre nom complet'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'رقم الهاتف' : 'Téléphone'}
                    </label>
                    <input
                      type="tel"
                      value={accountForm?.phone}
                      onChange={(e) => setAccountForm({ ...accountForm, phone: e?.target?.value })}
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                      placeholder="+212 6XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'اسم الوكالة' : "Nom de l'agence"}
                    </label>
                    <input
                      type="text"
                      value={accountForm?.agency}
                      onChange={(e) => setAccountForm({ ...accountForm, agency: e?.target?.value })}
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                      placeholder={isRTL ? 'اسم الوكالة' : "Nom de l'agence"}
                    />
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={accountLoading}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-60 disabled:cursor-not-allowed shadow-warm"
                    >
                      {accountLoading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Save" size={16} />}
                      {isRTL ? 'حفظ التغييرات' : 'Enregistrer les modifications'}
                    </button>
                  </div>
                </form>
              )}

              {/* ── Email Tab ── */}
              {activeTab === 'email' && (
                <form onSubmit={handleEmailSave} className="space-y-5">
                  <Alert msg={emailMsg} />
                  <div className="p-4 bg-muted/50 rounded-md border border-border text-sm text-muted-foreground flex items-start gap-2">
                    <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5 text-primary" />
                    <span>
                      {isRTL
                        ? 'سيتم إرسال رابط تأكيد إلى عنوان البريد الإلكتروني الجديد. يجب النقر عليه لتأكيد التغيير.'
                        : "Un lien de confirmation sera envoyé à la nouvelle adresse. Vous devrez cliquer dessus pour valider le changement."}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'البريد الإلكتروني الحالي' : 'Email actuel'}
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-muted text-muted-foreground text-sm cursor-not-allowed"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'البريد الإلكتروني الجديد' : 'Nouvel email'}
                    </label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e?.target?.value)}
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                      placeholder="nouveau@email.com"
                    />
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={emailLoading || newEmail === user?.email}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-60 disabled:cursor-not-allowed shadow-warm"
                    >
                      {emailLoading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Mail" size={16} />}
                      {isRTL ? 'تحديث البريد الإلكتروني' : "Mettre à jour l'email"}
                    </button>
                  </div>
                </form>
              )}

              {/* ── Security Tab ── */}
              {activeTab === 'security' && (
                <form onSubmit={handlePasswordSave} className="space-y-5">
                  <Alert msg={passwordMsg} />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'كلمة المرور الجديدة' : 'Nouveau mot de passe'}
                    </label>
                    <input
                      type="password"
                      value={passwordForm?.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e?.target?.value })}
                      required
                      minLength={8}
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                      placeholder={isRTL ? '8 أحرف على الأقل' : 'Au moins 8 caractères'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {isRTL ? 'تأكيد كلمة المرور' : 'Confirmer le mot de passe'}
                    </label>
                    <input
                      type="password"
                      value={passwordForm?.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e?.target?.value })}
                      required
                      className="w-full px-3 py-2.5 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-smooth"
                      placeholder={isRTL ? 'أعد إدخال كلمة المرور' : 'Répétez le mot de passe'}
                    />
                    {passwordForm?.confirmPassword && passwordForm?.newPassword !== passwordForm?.confirmPassword && (
                      <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                        <Icon name="AlertCircle" size={12} />
                        {isRTL ? 'كلمتا المرور غير متطابقتين' : 'Les mots de passe ne correspondent pas'}
                      </p>
                    )}
                  </div>

                  {/* Password strength hints */}
                  <div className="p-4 bg-muted/50 rounded-md border border-border space-y-2">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      {isRTL ? 'متطلبات كلمة المرور:' : 'Exigences du mot de passe :'}
                    </p>
                    {[
                      { check: passwordForm?.newPassword?.length >= 8, label: isRTL ? '8 أحرف على الأقل' : 'Au moins 8 caractères' },
                      { check: /[A-Z]/?.test(passwordForm?.newPassword), label: isRTL ? 'حرف كبير واحد على الأقل' : 'Au moins une majuscule' },
                      { check: /[0-9]/?.test(passwordForm?.newPassword), label: isRTL ? 'رقم واحد على الأقل' : 'Au moins un chiffre' },
                    ]?.map((req, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <Icon
                          name={req?.check ? 'CheckCircle' : 'Circle'}
                          size={13}
                          className={req?.check ? 'text-green-500' : 'text-muted-foreground'}
                        />
                        <span className={req?.check ? 'text-green-600' : 'text-muted-foreground'}>{req?.label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-60 disabled:cursor-not-allowed shadow-warm"
                    >
                      {passwordLoading ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Lock" size={16} />}
                      {isRTL ? 'تغيير كلمة المرور' : 'Changer le mot de passe'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserProfileSettings;
